import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import useEnrolPageProps from "@hooks/useEnrolPageProps.ts";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import useAppStateSync from "@/hooks/useAppStateSync";
import { useCheckoutContext } from "@/hooks/useCheckoutContext";
import useResetAtomicState from "@/hooks/useResetAtomicState";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { dispatchGAEvents } from "@/utils/events/dispatchGAEvents";
import { router } from "@inertiajs/react";
import { useAtom, useSetAtom } from "jotai";

import { Box, Button, ButtonGroup, SimpleGrid } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import {
    LocaleInterface,
    LocaleStateInterface
} from "@ts/interfaces/Checkout/LocaleInterface.ts";

import {
    checkoutAtom,
    checkoutStudentFlowAtom,
    isNewsletterSubEnabledAtom,
    isProvidedQueryParamsAtom,
    pageQueryParamsAtom,
    parentIdAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom.ts";

import { dispatchHSEvents } from "@utils/events/dispatchHSEevents";
import { getTrackingId, removeNullProperties } from "@utils/utils";

import ContentLayout from "@layouts/ContentLayout";

import { Text } from "@components/Text";

const buttonStyles = {
    borderRadius: "24px",
    background: "var(--euka-colors-gray-200)"
};

const buttonActiveStyles = {
    background: "white !important",
    borderColor: "var(--euka-colors-burntYellow-300)",
    boxShadow: "0 0 0 1px var(--euka-colors-burntYellow-200)",
    zIndex: 1
};

const StateContainer = () => {
    const { t } = useTranslation();
    const { hubspotEventCheckout, availableCountries, parentUser } =
        useCheckoutPageProps();
    const { resetCheckoutAtomicState } = useResetAtomicState();

    const navigate = useNavigate();
    const [, setSearchParams] = useSearchParams();
    const setIsProvidedQueryParams = useSetAtom(isProvidedQueryParamsAtom);

    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [checkoutStudentFlow] = useAtom(checkoutStudentFlowAtom);

    const [pathNavigateTo, setPathNavigateTo] = useState(
        CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
    );
    const [states, setStates] = useState<LocaleStateInterface[]>([]);

    const [parentId] = useAtom(parentIdAtom);

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();

    const {
        state: { checkout: checkoutAppState },
        updateFlows: { resetCheckoutAppState }
    } = useAppStateSync();

    const checkoutPurchasingAddon =
        checkoutAppState?.checkoutPurchasingAddon ?? null;
    const { checkoutStateMutationState, checkoutStateMutate } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(pathNavigateTo));
        });

    const isParentUserLoggedIn = !!parentUser;
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const [isNewsletterSubEnabled] = useAtom(isNewsletterSubEnabledAtom);
    const { hubspotEventEnrol } = useEnrolPageProps();

    const { fetchProductFamilies } = useCheckoutContext();

    const checkoutContext = useContext(CheckoutContext);

    const {
        userSelectedCountry,
        userSelectedLang,
        userSelectedState,
        isProductFamiliesLoading,
        onUpdateUserSelectedCountry,
        onUpdateUserSelectedState
    } = checkoutContext;

    // Show International option only when selected country is AU
    const filteredCountries = availableCountries.filter(country => {
        return country.value === "AU" || country.value === "international";
    });

    const onSaveData = () => {
        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                checkout_status: "ABANDON",
                email: checkout.user.email,
                checkout_token: userCheckoutStateToken,
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                checkout_state: userSelectedState
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        setCheckout(prev => ({
            ...prev,
            nextAttemptPage:
                userSelectedCountry === "international"
                    ? CheckoutPagesEnum.INTERNATIONAL_ADDON_PAGE
                    : CheckoutPagesEnum.PRODUCT_FAMILY_SELECT_PAGE
        }));

        fetchProductFamilies(userSelectedCountry, userSelectedLang);

        dispatchGAEvents({
            event: "select_state",
            country: userSelectedCountry,
            state: userSelectedState,
            parentId
        });

        checkoutStateMutate();
    };

    const onGoBack = () => {
        if (parentUser) {
            if (
                pageQueryParams.origin === "ecommerce" &&
                (checkout.selectedStudent || !checkout.student)
            ) {
                navigate(
                    routeWithQueryParams(
                        CheckoutPageRoutesEnum.STUDENT_SELECTION_PAGE
                    )
                );
            } else {
                dispatchGAEvents({
                    event: "drop_off",
                    eventCategory: "checkout",
                    eventAction: "go_back",
                    eventLabel: "Location Details",
                    parentId
                });

                router.get(route("parents.dashboard"));
                resetCheckoutAtomicState();
            }
        } else {
            navigate(
                routeWithQueryParams(CheckoutPageRoutesEnum.USER_INFO_PAGE)
            );
        }
    };

    const onCancel = () => {
        resetCheckoutAtomicState();
        resetCheckoutAppState(
            checkoutAppState?.checkoutRedirectsTo ??
                route("parents.manage-programs")
        );
    };

    useEffect(() => {
        if (checkoutStateMutationState.isSuccess) {
            // Update the related query parameters with the selected country and state.
            onSetupQueryParams({
                ...pageQueryParams,
                selectedCountry: userSelectedCountry,
                selectedState: userSelectedState,
                selectedLang: userSelectedLang
            });
        }
    }, [checkout, checkoutStateMutationState]);

    useEffect(() => {
        if (!userSelectedCountry && filteredCountries.length > 0) {
            onUpdateUserSelectedCountry(filteredCountries[0].value);
        }
    }, [filteredCountries]);

    useEffect(() => {
        const selectedCountryData = filteredCountries.find(
            country => country.value === userSelectedCountry
        );

        if (selectedCountryData) {
            setStates(selectedCountryData.states || []);
            if (
                !selectedCountryData.states.some(
                    state => state.value === userSelectedState
                )
            ) {
                onUpdateUserSelectedState("");
            }
        }
    }, [userSelectedCountry, filteredCountries]);

    useEffect(() => {
        if (checkoutPurchasingAddon) {
            setPathNavigateTo(
                userSelectedCountry === "international"
                    ? CheckoutPageRoutesEnum.INTERNATIONAL_ADDON_PAGE
                    : CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
            );
        }
    }, [checkoutPurchasingAddon, userSelectedCountry]);

    useEffect(() => {
        if (
            pageQueryParams.origin === "ecommerce" &&
            parentUser &&
            checkoutStudentFlow === null
        ) {
            navigate(
                routeWithQueryParams(
                    CheckoutPageRoutesEnum.STUDENT_SELECTION_PAGE
                )
            );
        }
    }, []);

    useEffect(() => {
        // Set all the query parameters in the URL which were updated in "pageQueryParams" atom at this point.
        setSearchParams(removeNullProperties(pageQueryParams));
        setIsProvidedQueryParams(false);
    }, [pageQueryParams]);

    useEffect(() => {
        // TODO: Improve hardcorded check for contries don't need state selections
        if (userSelectedCountry === "VN") {
            navigate(
                routeWithQueryParams(
                    CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                )
            );
        }
    }, []);

    const handleCountrySelect = (countryValue: string) => {
        onUpdateUserSelectedCountry(countryValue);
        const countryData = filteredCountries.find(
            country => country.value === countryValue
        );
        setStates(countryData?.states || []);
        onUpdateUserSelectedState("");
    };

    return (
        <ContentLayout
            title={t(
                "checkout.selectStatePage.title",
                "Where are you planning to HomeSchool?"
            )}
            description={t(
                "checkout.selectStatePage.desc",
                "This helps determine the homeschooling requirements based on where you’d like to homeschool your child"
            )}
            headerBackButton={!checkoutPurchasingAddon && isParentUserLoggedIn}
            footerActionsProps={{
                hasBackButton: !checkoutPurchasingAddon && isParentUserLoggedIn,
                hasCancelButton:
                    !!checkoutPurchasingAddon && isParentUserLoggedIn,
                isNextBtnLoading: checkoutStateMutationState.isPending,
                canGoNext:
                    userSelectedCountry === "international" ||
                    (!!userSelectedCountry &&
                        !!userSelectedState &&
                        !isProductFamiliesLoading),
                onGoNext: onSaveData,
                onGoBack,
                onCancel,
                btnClassHierarchy: {
                    next: [ClassesEnum.StateContainer, ClassesEnum.Button]
                },
                btnActions: {
                    next: "goNextFromStateSelectPage"
                }
            }}
            step={2}
            containerWidth={["95%", null, null, null, null]}
            className={ClassesEnum.StateContainer}
        >
            <Box
                w={{
                    base: "100%",
                    lg: userSelectedCountry === "international" ? "60%" : "40%"
                }}
                m="auto"
                textAlign="center"
            >
                <ButtonGroup
                    spacing="0"
                    ml={{ base: "-36px !important", md: "0 !important" }}
                    style={{ margin: "auto", marginBottom: "24px" }}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.StateContainer, ClassesEnum.Button],
                        "countryButtonGroup"
                    )}
                >
                    {filteredCountries.map((country: LocaleInterface) => (
                        <Button
                            key={country.value}
                            variant="outline"
                            style={{
                                ...buttonStyles,
                                marginRight: "-50px"
                            }}
                            px={14}
                            data-tracking-id={getTrackingId(
                                [
                                    ClassesEnum.StateContainer,
                                    ClassesEnum.Button
                                ],
                                `country${country.value}`
                            )}
                            onClick={() => handleCountrySelect(country.value)}
                            isActive={userSelectedCountry === country.value}
                            _active={buttonActiveStyles}
                            data-testid={`country-${country.value}`}
                        >
                            {country.label}
                        </Button>
                    ))}
                </ButtonGroup>

                {userSelectedCountry === "international" ? (
                    <Text>
                        {t(
                            "checkout.selectStatePage.descInternational",
                            "Please note if you’re considering homeschooling your child in Australia this year and need to be registered, even if you’re overseas or planning to be overseas temporarily, please select “Australia” and your intended state for registration."
                        )}
                    </Text>
                ) : (
                    <>
                        <Text type="subHeading" mb="16px" align="left">
                            {t(
                                "checkout.selectStatePage.selectStateTitle",
                                "Select your state"
                            )}
                        </Text>
                        <SimpleGrid
                            id="main_grid"
                            columns={{ base: 1, xl: 4 }}
                            gridGap="16px"
                        >
                            {states
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map(state => {
                                    const { value: stateId } = state;
                                    return (
                                        <Button
                                            key={stateId}
                                            variant="outline"
                                            style={buttonStyles}
                                            data-tracking-id={getTrackingId(
                                                [
                                                    ClassesEnum.StateContainer,
                                                    ClassesEnum.Button
                                                ],
                                                `state${stateId}`
                                            )}
                                            onClick={() =>
                                                onUpdateUserSelectedState(
                                                    stateId
                                                )
                                            }
                                            isActive={
                                                userSelectedState === stateId
                                            }
                                            _active={buttonActiveStyles}
                                            data-testid={`state-${stateId}`}
                                        >
                                            {stateId}
                                        </Button>
                                    );
                                })}
                        </SimpleGrid>
                    </>
                )}
            </Box>
        </ContentLayout>
    );
};

export default StateContainer;
